import React, { useState } from 'react';
import { Cpu, HardDrive, Database, Globe, Radio, MapPin, Shield, ShieldEllipsis, ShieldEllipsisIcon, HardDriveIcon, MemoryStick, UsersRound, HeartPlusIcon, DatabaseBackupIcon, LifeBuoyIcon } from 'lucide-react';
import './gmod.css';
import { Helmet } from "react-helmet";

interface PlanFeature {
  icon: React.ElementType;
  label: string;
  value: string;
}

interface PricingPlan {
  id: string;
  name: string;
  color: string;
  features: PlanFeature[];
  basePrice: number;
  popular?: boolean;
  orderLink: string;
}

interface PricingPeriod {
  id: string;
  label: string;
  months: number;
  discount: number;
  badge?: string;
}

// Composants SVG pour les mobs Minecraft
const Gmod = () => (
    <img
        src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/97/Garry%27s_Mod_logo.svg/2048px-Garry%27s_Mod_logo.svg.png"
        alt="Nova Life Icon"
        width={60}
        height={60}
        className="mob-icon"
    />
);

const ZombieIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_zombie)">
        <path d="M58 0L30 15L58 30L86 15L58 0ZM25 45L35 50V80L25 75V45ZM91 45V75L81 80V50L91 45ZM40 55V85L58 95L76 85V55L58 65L40 55ZM20 85L30 90V110L20 105V85ZM96 85V105L86 110V90L96 85ZM35 95V115L58 105L81 115V95L58 100L35 95Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_zombie">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const BlazeIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_blaze)">
        <path d="M58 10L48 20L58 30L68 20L58 10ZM40 25L30 35L40 45L50 35L40 25ZM76 25L86 35L76 45L66 35L76 25ZM58 40L48 50L58 60L68 50L58 40ZM25 45L15 55L25 65L35 55L25 45ZM91 45L101 55L91 65L81 55L91 45ZM40 70L30 80L40 90L50 80L40 70ZM76 70L86 80L76 90L66 80L76 70ZM58 85L48 95L58 105L68 95L58 85Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_blaze">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const SpiderIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_spider)">
        <path d="M58 30C70 30 80 40 80 52C80 64 70 74 58 74C46 74 36 64 36 52C36 40 46 30 58 30ZM25 45L15 35L10 40L20 50L25 45ZM91 45L96 40L106 35L101 45L91 45ZM20 52L10 47L5 52L15 57L20 52ZM96 52L101 57L111 52L106 47L96 52ZM25 59L20 64L10 74L15 79L25 69L25 59ZM91 59L91 69L101 79L106 74L96 64L91 59Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_spider">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const EnderIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_ender)">
        <path d="M58 5L45 15L58 25L71 15L58 5ZM35 20L22 30L35 40L48 30L35 20ZM81 20L94 30L81 40L68 30L81 20ZM58 35L45 45L58 55L71 45L58 35ZM22 50L9 60L22 70L35 60L22 50ZM94 50L107 60L94 70L81 60L94 50ZM45 65L32 75L45 85L58 75L45 65ZM71 65L84 75L71 85L58 75L71 65ZM58 95L45 105L58 115L71 105L58 95Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_ender">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const Price: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');

  const periods: PricingPeriod[] = [
    { id: 'monthly', label: 'Par mois', months: 1, discount: 0 },
    { id: '3months', label: '3 mois', months: 3, discount: 0, badge: '-5%' },
    { id: '6months', label: '6 mois', months: 6, discount: 0, badge: '-5%' },
    { id: '1year', label: '1 an', months: 12, discount: 0, badge: '-5%' }
  ];

  const plans: PricingPlan[] = [
    {
      id: 'Dev',
      name: 'Garrys Mod | Dev',
      color: 'green',
      basePrice: 1.99,
      orderLink: 'https://client.hostware.fr/store/basket/add/15',
      features: [
        { icon: Cpu, label: 'CPU', value: '2 vCPU' },
        { icon: MemoryStick, label: 'RAM', value: ' 6 Go RAM' },
        { icon: HardDriveIcon, label: '', value: '12 Go de stockage' },
        { icon: DatabaseBackupIcon, label: '1 Backup', value: '' },
        { icon: Radio, label: 'Bande passante', value: ' 10 GB/s' },
        { icon: LifeBuoyIcon, label: 'Support prioritaire', value: '' },
        { icon: ShieldEllipsisIcon, label: 'Protection Anti-DDos', value: 'Protection' }
      ]
    },
    {
      id: 'Starter',
      name: 'Garrys Mod | Starter',
      color: 'blue',
      basePrice: 4.99,
      orderLink: 'https://client.hostware.fr/store/basket/add/16',
      features: [
        { icon: Cpu, label: 'CPU', value: '3 vCPU' },
        { icon: MemoryStick, label: 'RAM', value: ' 8 Go RAM' },
        { icon: HardDriveIcon, label: '', value: '25 Go de stockage' },
        { icon: DatabaseBackupIcon, label: '1 Backup', value: '' },
        { icon: Radio, label: 'Bande passante', value: ' 10 GB/s' },
        { icon: LifeBuoyIcon, label: 'Support prioritaire', value: '' },
        { icon: ShieldEllipsisIcon, label: 'Protection Anti-DDos', value: 'Protection' }
      ]
    },
    {
      id: 'Premium',
      name: 'Garrys Mod | Premium',
      color: 'yellow',
      basePrice: 9.99,
      orderLink: 'https://client.hostware.fr/store/basket/add/17',
      popular: true,
      features: [
        { icon: Cpu, label: 'CPU', value: '5 vCPU' },
        { icon: MemoryStick, label: 'RAM', value: ' 16 Go RAM' },
        { icon: HardDriveIcon, label: '', value: '50 Go de stockage' },
        { icon: DatabaseBackupIcon, label: '1 Backup', value: '' },
        { icon: Radio, label: 'Bande passante', value: ' 10 GB/s' },
        { icon: LifeBuoyIcon, label: 'Support prioritaire', value: '' },
        { icon: ShieldEllipsisIcon, label: 'Protection Anti-DDos', value: 'Protection' }
      ]
    },
    {
      id: 'Ultra',
      name: 'Garrys Mod | Ultra',
      color: 'red',
      basePrice: 14.5,
      orderLink: 'https://client.hostware.fr/store/basket/add/18',
      features: [
        { icon: Cpu, label: 'CPU', value: '7 vCPU' },
        { icon: MemoryStick, label: 'RAM', value: ' 32 Go RAM' },
        { icon: HardDriveIcon, label: '', value: '70 Go de stockage' },
        { icon: DatabaseBackupIcon, label: '1 Backup', value: '' },
        { icon: Radio, label: 'Bande passante', value: ' 10 GB/s' },
        { icon: LifeBuoyIcon, label: 'Support prioritaire', value: '' },
        { icon: ShieldEllipsisIcon, label: 'Protection Anti-DDos', value: 'Protection' }
      ]
    }
  ];

  const getMobIcon = (planId: string) => {
    switch (planId) {
      case 'gmod': return <Gmod />;
      case 'zombie': return <ZombieIcon />;
      case 'blaze': return <BlazeIcon />;
      case 'spider': return <SpiderIcon />;
      case 'ender': return <EnderIcon />;
      default: return <Gmod />;
    }
  };

  const calculatePrice = (basePrice: number, selectedPeriodId: string, planId: string) => {
    const period = periods.find(p => p.id === selectedPeriodId);
    if (!period) return { price: basePrice, total: basePrice, savings: 0 };

    // Appliquer 5% de réduction uniquement sur Lite et Premium pour 3m/6m/1an
    const eligible = planId === 'Ultra' || planId === 'Premium';
    const discount = (period.id !== 'monthly' && eligible) ? 5 : 0;

    const discountedPrice = basePrice * (1 - discount / 100);
    const totalPrice = discountedPrice * period.months;

    return {
      price: discountedPrice,
      total: totalPrice,
      savings: (period.months > 1 && discount > 0) ? (basePrice * period.months - totalPrice) : 0
    };
  };

  const getSelectedPeriod = () => periods.find(p => p.id === selectedPeriod);

  return (
      <>
        <Helmet>
          <title>HOSTWARE® – Hébergement Serveur Gmod performant dès 1.99€/mois</title>
          <meta
              name="description"
              content="Louez un serveur Garry's Mod performant avec Anti-DDoS, accès SSH, sauvegardes automatiques et support 24/7. Offres dès 1.99€/mois chez HostWare."
          />
          <meta
              name="keywords"
              content="hébergement gmod, serveur garry's mod, serveur gaming, hébergement jeu, gmod hosting, vps gaming, hébergement pas cher, serveurs gmod performants"
          />
          <meta
              name="title"
              content="Serveur Garry's Mod performant & fiable - HostWare"
          />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="HostWare" />
          <meta property="og:locale" content="fr_FR" />
          <meta property="og:url" content="https://hostware.fr/gmod" />
          <meta property="og:title" content="HOSTWARE – Hébergement Serveur Gmod performant dès 1.99€/mois" />
          <meta
              property="og:description"
              content="Louez un serveur Garry's Mod performant avec Anti-DDoS, sauvegardes automatiques et support 24/7. Offres dès 1.99€/mois."
          />
          <meta property="og:image" content="https://hostware.fr/logo512.png" />

          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:title" content="HOSTWARE – Hébergement Serveur Gmod performant dès 1.99€/mois" />
          <meta
              property="twitter:description"
              content="Louez un serveur Garry's Mod performant avec Anti-DDoS, sauvegardes automatiques et support 24/7. Offres dès 1.99€/mois."
          />
          <meta property="twitter:image" content="https://hostware.fr/logo512.png" />
          <meta property="twitter:image:width" content="512" />
          <meta property="twitter:image:height" content="512" />
          <meta property="twitter:image:alt" content="Logo HostWare" />
        </Helmet>

        <div className="pricing-container">
          {/* Background Effects */}
          <div className="background-overlay"></div>
          <div className="background-orb orb-1"></div>
          <div className="background-orb orb-2"></div>
          <div className="floating-particles"></div>

          {/* Hero Section */}
          <div className="hero-section">
            <div className="hero-content">
              <div className="rocket-icon animate-bounce-slow">🚀</div>
              <h1 className="hero-title animate-fade-in-up">
                DÉCOUVREZ NOS OFFRES
              </h1>
              <p className="hero-subtitle animate-fade-in-up" style={{animationDelay: '0.2s'}}>
                Louez votre serveur Nova Life : abmoise pour jouer avec vos amis ou en communauté. Profitez de performances exceptionnelles pour une expérience de jeu inoubliable.
              </p>

              {/* Duration Tabs */}
              <div className="duration-tabs animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                {periods.map((period) => (
                    <button
                        key={period.id}
                        onClick={() => setSelectedPeriod(period.id)}
                        className={`duration-tab ${selectedPeriod === period.id ? 'active' : ''}`}
                    >
                      {period.label}
                      {period.badge && (
                          <span className="discount-badge animate-pulse-soft">
                      {period.badge}
                    </span>
                      )}
                    </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="pricing-section">
            <div className="plans-grid">
              {plans.map((plan, index) => {
                const pricing = calculatePrice(plan.basePrice, selectedPeriod, plan.id);
                const currentPeriod = getSelectedPeriod();

                return (
                    <div
                        key={plan.id}
                        className={`plan-card ${plan.color} ${plan.popular ? 'popular' : ''} animate-fade-in-up`}
                        style={{animationDelay: `${0.1 * index}s`}}
                    >
                      {plan.popular && (
                          <div className="popular-badge animate-glow">
                            {/* Utilisez le pseudo-élément CSS pour le contenu */}
                            <span className="sr-only">Populaire</span>
                          </div>
                      )}

                      {/* Plan Header */}
                      <div className="plan-header">
                        <div className="plan-icon-container">
                          <Gmod /> {/* Affiche toujours Gmod */}
                        </div>
                        <h3 className="plan-name">{plan.name}</h3>
                      </div>

                      {/* Features */}
                      <div className="plan-features">
                        {plan.features.map((feature, featureIndex) => (
                            <div
                                key={featureIndex}
                                className="feature-row animate-slide-in-left"
                                style={{animationDelay: `${0.6 + featureIndex * 0.1}s`}}
                            >
                              <feature.icon className="feature-icon" size={16} />
                              <span className="feature-label">{feature.label}</span>
                              <span className="feature-value">{feature.value}</span>
                            </div>
                        ))}
                      </div>

                      {/* Pricing */}
                      <div className="plan-pricing">
                        <div className="price-display">
                          <div className="price animate-number-change">
                            {pricing.price.toFixed(2)} €
                          </div>
                          <div className="duration">{currentPeriod?.label}</div>
                        </div>

                        {currentPeriod && currentPeriod.months > 1 && (
                            <div className="pricing-details">
                              <div className="total-price">
                                Total: {pricing.total.toFixed(2)} €
                              </div>
                              {pricing.savings > 0 && (
                                  <div className="savings animate-pulse-soft">
                                    Économie: {pricing.savings.toFixed(2)} €
                                  </div>
                              )}
                            </div>
                        )}
                      </div>

                      {/* CTA Button */}
                      <a
                          href={plan.orderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="plan-button animate-button-hover"
                      >
                        <span>Commander</span>
                        <div className="button-shine"></div>
                      </a>
                    </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
  );
};

export default Price;